import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import ListFieldMobileLabel from '#/modules/commonComponents/form/ListFieldMobileLabel';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import FloatingSelect from '#/modules/commonComponents/form/select';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import { Button, DESTRUCTIVE_GHOST_BUTTON_CLASS } from '#/modules/commonComponents/button';
import { ActionHintIcon } from '#/modules/commonComponents/hint';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';

const SPEC_LINE_GRID = '@3xl/form-section:grid-cols-[minmax(6rem,1fr)_minmax(8rem,1.4fr)]';
const CUSTOM_LINE_GRID = '@3xl/form-section:grid-cols-[minmax(6rem,1fr)_minmax(8rem,1.4fr)_auto]';

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-[var(--z-content-sticky)]',
  'hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid',
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 gap-2 px-3 py-2.5',
  'border-b border-gray-200 last:border-b-0',
  '@3xl/form-section:items-center',
);

/** 子清單內捲：覆寫 scroll token，走 --form-list-chrome；禁 ad-hoc max-h-*／form-list--window */
const SPEC_SUBLIST_SCROLL_STYLE = {
  '--form-section-scroll-max-height':
    'min(32rem, calc(100vh - var(--form-list-chrome)))',
};

/** 無 label FloatingSelect：有錯才 form_error；placeholder 紅字走 buildSelectStyles */
function SpecValueSelect({
  id,
  control,
  errors,
  options,
  allowEmpty,
  isClearable,
  placeholder,
  noOptionsMessage,
  mobileLabel,
}) {
  const fieldError = getNestedError(errors, id);
  const hasError = Boolean(fieldError);

  return (
    <div className="min-w-0 w-full">
      {mobileLabel ? (
        <ListFieldMobileLabel hasError={hasError}>
          {mobileLabel}
        </ListFieldMobileLabel>
      ) : null}
      <FloatingSelect
        id={id}
        options={options}
        control={control}
        errors={errors}
        allowEmpty={allowEmpty}
        isClearable={isClearable}
        placeholder={placeholder}
        noOptionsMessage={noOptionsMessage}
        containerClassName="w-full"
      />
    </div>
  );
}

SpecValueSelect.propTypes = {
  id: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  allowEmpty: PropTypes.bool,
  isClearable: PropTypes.bool,
  placeholder: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  mobileLabel: PropTypes.string,
};

function getSpecDisplayName(entry, index) {
  return entry.name || entry.label || `規格 #${index + 1}`;
}

const MtrSpecValuesEditor = ({
  control,
  register,
  errors,
  specEntries = [],
  specSelectOptionsMap = {},
}) => {
  const {
    fields: customFields,
    append: appendCustom,
    remove: removeCustom,
  } = useFieldArray({
    control,
    name: 'specCustom',
  });

  return (
    <div className="form-section @container/form-section w-full">
      <div className="form-section__header">
        <h2 className="form-block__title mb-0!">規格值</h2>
      </div>

      <div
        className={cn(
          'form-section__body',
          'mt-0!',
          'grid grid-cols-1 gap-5',
          'lg:grid-cols-2',
        )}
      >
        <div className="min-w-0">
          <h3 className="form-subsection__title form-subsection__title--spaced mt-2">規格</h3>

          {specEntries.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">請先選擇物料模板</p>
          ) : (
            <div
              className="form-section__scroll"
              style={SPEC_SUBLIST_SCROLL_STYLE}
            >
              <div className={cn(LINE_HEADER_CLASS, SPEC_LINE_GRID)}>
                <span className="inline-flex items-center gap-1">
                  規格名稱
                  <ActionHintIcon
                    tip="名稱後方有紅色 * 表示此規格必填"
                    ariaLabel="規格名稱說明"
                  />
                </span>
                <span>規格值</span>
              </div>
              <div className="overflow-x-auto">
                <ul className="m-0 list-none p-0">
                  {specEntries.map((entry, idx) => {
                    const optionPath = `specEntries.${idx}.mtrSpecOptionId`;
                    const inputPath = `specEntries.${idx}.inputValue`;

                    return (
                      <li
                        key={entry.mtrSpecId || `spec-${idx}`}
                        className={cn(LINE_ROW_CLASS, SPEC_LINE_GRID)}
                      >
                        <div className="min-w-0">
                          <ListFieldMobileLabel>
                            規格名稱
                          </ListFieldMobileLabel>
                          <div className="inline-flex max-w-full min-w-0 items-start gap-0.5">
                            <MaterialNameStack
                              name={getSpecDisplayName(entry, idx)}
                              code={entry.code || ''}
                            />
                            {entry.required ? (
                              <span
                                className="shrink-0 text-sm font-medium text-destructive"
                                aria-label="必填"
                              >
                                *
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className="min-w-0 mt-2 @3xl/form-section:mt-0">
                          {entry.specType === 'SELECT' ? (
                            <SpecValueSelect
                              id={optionPath}
                              options={specSelectOptionsMap[entry.mtrSpecId] ?? []}
                              control={control}
                              errors={errors}
                              allowEmpty={!entry.required}
                              isClearable={!entry.required}
                              placeholder="請選擇"
                              noOptionsMessage="無可用選項"
                              mobileLabel="規格值"
                            />
                          ) : (
                            <PlainRegisteredInput
                              id={inputPath}
                              register={register}
                              errors={errors}
                              placeholder="請輸入"
                              mobileLabel="規格值"
                            />
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="min-w-0">
          <div className="form-section__header">
            <h3 className="form-subsection__title mb-0!">自訂規格</h3>
            <Button
              type="button"
              size="sm"
              onClick={() => appendCustom({ customKey: '', inputValue: '' })}
            >
              新增自訂項
            </Button>
          </div>

          {customFields.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">可選：新增自訂鍵值對</p>
          ) : (
            <div
              className="form-section__scroll"
              style={SPEC_SUBLIST_SCROLL_STYLE}
            >
              <div
                className={cn(LINE_HEADER_CLASS, CUSTOM_LINE_GRID)}
                aria-hidden
              >
                <span>自訂名稱</span>
                <span>規格值</span>
                <span className="sr-only text-right">移除</span>
              </div>
              <div className="overflow-x-auto">
                <ul className="m-0 list-none p-0">
                  {customFields.map((field, idx) => (
                    <li
                      key={field.id}
                      className={cn(LINE_ROW_CLASS, CUSTOM_LINE_GRID)}
                    >
                      <div className="min-w-0">
                        <PlainRegisteredInput
                          id={`specCustom.${idx}.customKey`}
                          register={register}
                          errors={errors}
                          placeholder="自訂名稱"
                          mobileLabel="自訂名稱"
                        />
                      </div>
                      <div className="min-w-0">
                        <PlainRegisteredInput
                          id={`specCustom.${idx}.inputValue`}
                          register={register}
                          errors={errors}
                          placeholder="規格值"
                          mobileLabel="規格值"
                        />
                      </div>
                      <div className="flex min-w-0 items-center justify-end @3xl/form-section:pt-0 pt-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                          aria-label="移除自訂規格"
                          onClick={() => removeCustom(idx)}
                        >
                          <PiTrashBold className="size-4" aria-hidden />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

MtrSpecValuesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  specEntries: PropTypes.arrayOf(PropTypes.object),
  specSelectOptionsMap: PropTypes.object,
};

export default MtrSpecValuesEditor;
